import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { IonIcon, IonSpinner } from '@ionic/react';
import { closeOutline, searchOutline, checkmarkOutline } from 'ionicons/icons';
import styles from './GlassPicker.module.scss';
import { useTranslation } from 'react-i18next';

export interface GlassPickerOption {
  label: string;
  value: string | number;
  sublabel?: string;
  badge?: string;
  group?: string;   // section header — rendered above the first item in each group
  content?: React.ReactNode; // extra content rendered below label when this row is selected
}

interface GlassPickerProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  options: GlassPickerOption[];
  value?: string | number | null | undefined;
  onSelect?: (value: string | number) => void;
  searchable?: boolean;
  loading?: boolean;
  /** When provided: rows don't auto-close; shows CANCEL / SAVE footer */
  onSave?: () => void;
  /** Show radio buttons on left instead of checkmark on right */
  radioMode?: boolean;
  /** Multi-select mode: array of selected values */
  multiValue?: (string | number)[];
  onMultiToggle?: (value: string | number) => void;
}

const GlassPicker: React.FC<GlassPickerProps> = ({
  isOpen, onClose, title, subtitle, options, value, onSelect, searchable = false, loading = false,
  onSave, radioMode = false, multiValue, onMultiToggle,
}) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');

  if (!isOpen) return null;

  const filtered = searchable && search.trim()
    ? options.filter(o =>
        o.label.toLowerCase().includes(search.toLowerCase()) ||
        (o.sublabel ?? '').toLowerCase().includes(search.toLowerCase())
      )
    : options;

  return createPortal(
    <div className={styles.backdrop} onClick={onClose} onTouchMove={e => e.stopPropagation()}>
      <div className={styles.card} onClick={e => e.stopPropagation()} onTouchMove={e => e.stopPropagation()}>

        <div className={styles.header}>
          <button className={styles.close} onClick={onClose}>
            <IonIcon icon={closeOutline} />
          </button>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <span className={styles.title}>{title}</span>
            {subtitle && <span style={{ fontSize: '0.75rem', color: 'var(--ion-color-medium)', marginTop: 2 }}>{subtitle}</span>}
          </div>
          <span style={{ width: 36 }} />
        </div>

        {searchable && (
          <div className={styles.searchWrap}>
            <IonIcon icon={searchOutline} className={styles.searchIcon} />
            <input
              className={styles.searchInput}
              placeholder={t('components.GlassPicker.search')}
              value={search}
              onChange={e => setSearch(e.target.value)}
              autoFocus
            />
          </div>
        )}

        <div className={styles.list}>
          {loading ? (
            <div className={styles.loading}><IonSpinner name="bubbles" color="primary" /></div>
          ) : filtered.length === 0 ? (
            <div className={styles.empty}>
              {search ? t('components.GlassPicker.noResultsMatchYourSearch') : t('components.GlassPicker.noOptionsAvailable')}
            </div>
          ) : (
            filtered.map((o, idx) => {
              const selected = multiValue !== undefined
                ? multiValue.includes(o.value)
                : o.value === value;
              const showHeader = o.group && (idx === 0 || filtered[idx - 1].group !== o.group);
              return (
                <React.Fragment key={o.value}>
                  {showHeader && (
                    <div className={styles.sectionHeader}>{o.group}</div>
                  )}
                  <div
                    className={`${styles.row} ${selected && multiValue === undefined ? styles.rowSelected : ''}`}
                    onClick={() => {
                      if (multiValue !== undefined && onMultiToggle) {
                        onMultiToggle(o.value);
                      } else if (onSelect) {
                        onSelect(o.value);
                        if (!o.content && !onSave) onClose();
                      }
                    }}
                    style={radioMode ? { alignItems: 'flex-start', gap: 12 } : undefined}
                  >
                    {radioMode && (
                      <div style={{
                        width: 20, height: 20, borderRadius: '50%', flexShrink: 0, marginTop: 2,
                        border: `2px solid ${selected ? 'var(--ion-color-primary)' : 'var(--ion-color-medium)'}`,
                        background: selected ? 'var(--ion-color-primary)' : 'transparent',
                        boxShadow: selected ? 'inset 0 0 0 3px var(--glass-morphism-bg-tab-bar, #1a1a2e)' : 'none',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }} />
                    )}
                    <div className={styles.rowInfo} style={{ flex: 1 }}>
                      <div className={styles.rowLabel}>{o.label}</div>
                      {o.sublabel && <div className={styles.rowSublabel}>{o.sublabel}</div>}
                      {o.content && (
                        <div onClick={e => e.stopPropagation()} style={{ marginTop: 10 }}>
                          {o.content}
                        </div>
                      )}
                    </div>
                    {!radioMode && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {o.badge && <span className={styles.rowBadge}>{o.badge}</span>}
                        {selected && <IonIcon icon={checkmarkOutline} className={styles.rowCheck} />}
                      </div>
                    )}
                  </div>
                </React.Fragment>
              );
            })
          )}
        </div>

        {onSave && (
          <div style={{
            display: 'flex',
            borderTop: '1px solid var(--glass-wrapper-border-color, rgba(255,255,255,0.1))',
            flexShrink: 0,
          }}>
            <button
              onClick={onClose}
              style={{
                flex: 1, padding: '14px 0', background: 'transparent', border: 'none',
                borderRight: '1px solid var(--glass-wrapper-border-color, rgba(255,255,255,0.1))',
                fontSize: 14, fontWeight: 600, color: 'var(--ion-color-medium)', cursor: 'pointer',
                letterSpacing: '0.05em',
              }}
            >{t('components.GlassPicker.cancel')}</button>
            <button
              onClick={onSave}
              style={{
                flex: 1, padding: '14px 0', background: 'transparent', border: 'none',
                fontSize: 14, fontWeight: 600, color: 'var(--ion-color-primary)', cursor: 'pointer',
                letterSpacing: '0.05em',
              }}
            >{t('components.GlassPicker.save')}</button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default GlassPicker;
